import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, useSlotRecipe } from '@chakra-ui/react';
import { Sheet, type SheetRef } from 'react-modal-sheet';
import { useKeyboardBottomInset } from '../../hooks/useKeyboardBottomInset';
import { MOBILE_TAB_BAR_HEIGHT } from '../layout/MobileBottomNav';
import { ProgressiveBlur } from '../ProgressiveBlur';
import { sheetSlotRecipe } from '../../theme/recipes/sheet';

// Z-index stack (sheets cover the tab bar like native modals):
//   mobile tab bar + idle filter FAB → 1250 / 1260 (see MobileBottomNav)
//   filter / support sheets          → 1300
//   filter FAB while sheet open      → 1310 (close control stays tappable)
//   card detail sheet                → 1400
//   save / publish dialogs           → 1480 (deck builder mobile)
//   floating edge arrows             → 10032 (always on top of any sheet)
//   dev controls FAB/drawer          → 10100+ (see devControls DEV_CONTROLS_Z)
export const SHEET_Z_FILTER = 1300;
export const SHEET_Z_CARD = 1400;
/** Save draft / Publish — stacks above deck list & legality sheets in deck builder */
export const SHEET_Z_DECK_DIALOG = 1480;

interface RiftboundBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after the close animation finishes (useful to navigate without janking dismiss). */
  onCloseEnd?: () => void;
  children: React.ReactNode;
  /** Optional header content rendered inside the sheet header area (below the drag pill). */
  header?: React.ReactNode;
  /**
   * `docked` (default): the header takes its own row above the scroller.
   * `overlay`: the body scrolls beneath a frosted header that fades downward
   * (progressive blur, like the mobile app header); the scroller is padded by its height.
   */
  headerPlacement?: 'docked' | 'overlay';
  /** Pinned UI over the sheet (e.g. collection FAB); positioned relative to the sheet panel. */
  floatingAction?: React.ReactNode;
  /** Horizontal placement for floatingAction (`center`: portrait FAB; `end`: right-aligned, e.g. landscape). */
  floatingActionPlacement?: 'center' | 'end';
  /** Override the CSS z-index applied to backdrop + container. */
  zIndex?: number;
  /**
   * `full-height` — nearly full viewport; uses `snapPoints` for draggable stops (default behavior).
   * `content-height` — height follows children until max viewport, then scrolls (omit snap stops).
   * @see https://github.com/Temzasse/react-modal-sheet#detents
   */
  detent?: 'full-height' | 'content-height';
  /**
   * `modal` renders a blocking backdrop. `tray` behaves like persistent bottom UI:
   * no backdrop, so the page behind remains visually and physically interactive.
   */
  interactionMode?: 'modal' | 'tray';
  /**
   * Which snap position to open at (0 = first / largest).
   * Default: 0 (opens fully expanded).
   */
  initialSnap?: number;
  /**
   * Custom snap points as fractions of viewport height. Default: [0.95, 0.6, 0.2, 0].
   * Pass `false` to disable intermediate snap stops and use drag-to-close behavior.
   * Ignored when `detent` is `"content-height"` — the sheet hugs content instead.
   */
  snapPoints?: number[] | false;
  /**
   * Phone landscape: inset the sheet from the left/right edges (see bottom-sheet.css).
   * Use for wide panels like card detail; omit for full-bleed filter sheets.
   */
  landscapeHorizontalInset?: boolean;
  /**
   * When true, the scroller adds bottom padding equal to the mobile tab bar height.
   * Prefer false — modal sheets sit above the tab bar; only tray-style UI that
   * leaves tabs visible should reserve space.
   */
  reserveTabBar?: boolean;
  /**
   * Stretch children to the sheet's remaining height (flex-column scroller).
   * Use when an inner list should fill leftover space and scroll itself.
   */
  fillContent?: boolean;
  /** Temporarily disable sheet drag gestures while nested interactions own the pointer. */
  disableDrag?: boolean;
  /**
   * Programmatic snap index into `snapPoints` while open (e.g. collapse during binder DnD).
   * Ignored when snap stops are disabled.
   */
  snapIndex?: number;
  /** Backdrop hit-testing — `none` lets pointer events reach content behind the sheet (binder pockets). */
  backdropPointerEvents?: 'auto' | 'none';
  /**
   * Paint the sheet in `bgElevated` so the header and scrolling body are one surface.
   * Use when the page behind is `bg` and the sheet should read as a raised panel.
   */
  elevated?: boolean;
  /** Fired when the user snaps the sheet to a new stop (including programmatic snaps). */
  onSnapIndexChange?: (index: number) => void;
  /**
   * Pinned under the scroller, in the visible sheet. Use for actions that must
   * stay on screen while the body scrolls. Requires a full-height snap (`1`)
   * so the sheet bottom is the screen bottom.
   */
  footer?: React.ReactNode;
  /**
   * `docked` (default): the footer takes its own row under the scroller.
   * `overlay`: the body scrolls beneath a frosted footer that fades upward
   * (progressive blur, like the mobile header); the scroller is padded by its height.
   */
  footerPlacement?: 'docked' | 'overlay';
  /**
   * Override the sheet element height. The library defaults to
   * `100% - safe-area - 34px`, which caps how far a snap can open.
   * Snap fractions are of this height.
   */
  containerHeight?: string;
}

/**
 * DOM node inside the sheet header (the drag surface).
 * Portaled UI placed here — deck tabs — moves the sheet instead of scrolling its body.
 */
const SheetHeaderSlotContext = createContext<HTMLElement | null>(null);

export function useSheetHeaderSlot(): HTMLElement | null {
  return useContext(SheetHeaderSlotContext);
}

const DEFAULT_SNAP_POINTS = [0.95, 0.6, 0.2, 0];

/** Live `offsetHeight` of the element handed to the returned callback ref. */
function useMeasuredHeight(): [(node: HTMLDivElement | null) => void, number] {
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setHeight(node.offsetHeight));
    observer.observe(node);
    observerRef.current = observer;
  }, []);
  return [ref, height];
}

// Same curve as the mobile app header: hold the frost through the controls,
// then taper out so there is no hard edge against the scrolling body.
const FROST_MASK_STOPS = 'black 0%, black 48%, rgba(0,0,0,0.75) 72%, transparent 100%';

/** Frosted backing for overlay header/footer chrome; sits under the chrome's own content. */
function SheetFrost({ edge, elevated }: { edge: 'top' | 'bottom'; elevated: boolean }) {
  const mask = `linear-gradient(to ${edge === 'top' ? 'bottom' : 'top'}, ${FROST_MASK_STOPS})`;
  return (
    <ProgressiveBlur
      edge={edge}
      blur={36}
      bg={elevated ? 'bgElevated/60' : 'bg/60'}
      top={0}
      bottom={0}
      h="auto"
      zIndex={-1}
      // Container has a 16px top radius and 1px top border.
      borderTopRadius={edge === 'top' ? '15px' : undefined}
      css={{ maskImage: mask, WebkitMaskImage: mask }}
    />
  );
}

export function RiftboundBottomSheet({
  isOpen,
  onClose,
  onCloseEnd,
  children,
  header,
  headerPlacement = 'docked',
  floatingAction,
  floatingActionPlacement = 'end',
  zIndex = SHEET_Z_FILTER,
  detent = 'full-height',
  interactionMode = 'modal',
  initialSnap = 0,
  snapPoints: snapPointsProp,
  landscapeHorizontalInset = false,
  reserveTabBar = false,
  fillContent = false,
  disableDrag = false,
  snapIndex,
  backdropPointerEvents = 'auto',
  onSnapIndexChange,
  elevated = false,
  containerHeight,
  footer,
  footerPlacement = 'docked',
}: RiftboundBottomSheetProps) {
  const sheetRef = useRef<SheetRef>(null);
  const sheetRecipe = useSlotRecipe({ recipe: sheetSlotRecipe });
  const sheetChrome = sheetRecipe({ elevated });
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
  const keyboardInset = useKeyboardBottomInset(isOpen);
  const scrollerPaddingBottom = reserveTabBar
    ? `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`
    : 'env(safe-area-inset-bottom, 0px)';
  const dockedFooter = footer && footerPlacement === 'docked' ? footer : null;
  const overlayFooter = footer && footerPlacement === 'overlay' ? footer : null;
  const overlayHeader = headerPlacement === 'overlay';
  const [measureOverlayHeader, overlayHeaderHeight] = useMeasuredHeight();
  const [measureOverlayFooter, overlayFooterHeight] = useMeasuredHeight();

  const useSnapStops = detent === 'full-height' && snapPointsProp !== false;
  const resolvedSnapPoints = useSnapStops ? (snapPointsProp ?? DEFAULT_SNAP_POINTS) : undefined;
  const hasSnapStops = Boolean(resolvedSnapPoints?.length);
  const isTray = interactionMode === 'tray';
  const backdropChildren = isTray
    ? []
    : [
        <Sheet.Backdrop
          key="backdrop"
          className="react-modal-sheet-backdrop"
          // Explicit inline zIndex overrides the library's hardcoded default of 1
          style={{ zIndex, pointerEvents: backdropPointerEvents }}
          onTap={onClose}
        />,
      ];

  /** Re-apply snap after layout — avoids opening with height = 0 (invisible sheet). */
  const snapToInitialPosition = useCallback(() => {
    sheetRef.current?.snapTo(initialSnap);
  }, [initialSnap]);

  // react-modal-sheet's internal open hook reads sheet height synchronously before
  // the container lays out — height is often 0 on first paint, so the animation
  // targets the wrong Y. Flush once geometry exists (matches library StrictMode caveat).
  useEffect(() => {
    if (!isOpen || !hasSnapStops) return;
    let cancelled = false;
    const settle = () => {
      if (!cancelled) snapToInitialPosition();
    };
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(settle);
    });
    const t = window.setTimeout(settle, 64);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(t);
    };
  }, [isOpen, hasSnapStops, snapToInitialPosition]);

  useEffect(() => {
    if (!isOpen || snapIndex === undefined || !hasSnapStops) return;
    sheetRef.current?.snapTo(snapIndex);
  }, [isOpen, snapIndex, hasSnapStops]);

  return (
    <SheetHeaderSlotContext.Provider value={headerSlot}>
    <Sheet
      ref={sheetRef}
      isOpen={isOpen}
      onClose={onClose}
      onCloseEnd={onCloseEnd}
      detent={detent}
      disableDrag={disableDrag}
      {...(hasSnapStops
        ? {
            snapPoints: resolvedSnapPoints,
            initialSnap,
            onOpenEnd: snapToInitialPosition,
            onSnap: onSnapIndexChange,
          }
        : {})}
      // Pass zIndex here so the library's internal useTransform picks it up
      // for the container's framer-motion z-index value.
      style={{ zIndex, '--rsm-z-index': zIndex } as React.CSSProperties}
    >
      <Sheet.Container
        key="container"
        className={[
          'react-modal-sheet-container',
          landscapeHorizontalInset ? 'riftbound-sheet-landscape-inset' : '',
          detent === 'content-height' ? 'riftbound-sheet-content-detent' : '',
          elevated ? 'riftbound-sheet-elevated' : '',
        ].filter(Boolean).join(' ')}
        style={{
          // Keep library `position: absolute; bottom: 0` — do not set position here or the sheet docks to the top.
          zIndex: zIndex + 1,
          // Lift above the software keyboard so focused inputs stay visible.
          ...(keyboardInset > 0 ? { marginBottom: keyboardInset } : null),
          ...(containerHeight ? { height: containerHeight } : null),
        }}
      >
        <Sheet.Header
          ref={overlayHeader ? measureOverlayHeader : undefined}
          className="react-modal-sheet-header"
          style={
            overlayHeader
              ? { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5 }
              : undefined
          }
        >
          {overlayHeader ? <SheetFrost edge="top" elevated={elevated} /> : null}
          <Box position="relative" h={header ? undefined : '20px'} pt={header ? 6 : 0}>
            {/* Drag pill. With no title, center it so the space above and below matches. */}
            <Box
              css={sheetChrome.handle}
              mx="auto"
              position="absolute"
              top={header ? 2 : '50%'}
              left={0}
              right={0}
              opacity={0.6}
              transform={header ? undefined : 'translateY(-50%)'}
            />
            {header && (
              <Box
                w="100%"
                pt={2}
                pb={overlayHeader ? 2 : elevated ? 3 : undefined}
                borderBottomWidth={elevated && !overlayHeader ? 'hairline' : undefined}
                borderColor={elevated && !overlayHeader ? 'border' : undefined}
              >
                {header}
              </Box>
            )}
          </Box>
          {/* Drag surface for portaled chrome (deck tabs). Empty until something portals in. */}
          <Box ref={setHeaderSlot} w="100%" />
        </Sheet.Header>

        <Sheet.Content className="react-modal-sheet-content">
          <Sheet.Scroller
            autoPadding={hasSnapStops && !fillContent && !dockedFooter}
            draggableAt={hasSnapStops ? 'both' : 'top'}
            style={{
              ...(overlayHeader ? { paddingTop: overlayHeaderHeight } : null),
              paddingBottom: dockedFooter
                ? 0
                : overlayFooter
                  ? overlayFooterHeight
                  : scrollerPaddingBottom,
              overflowX: 'hidden',
              ...(dockedFooter
                ? {
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: '0%',
                    minHeight: 0,
                    height: 'auto',
                  }
                : null),
              ...(fillContent
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'hidden',
                    height: dockedFooter ? 'auto' : '100%',
                  }
                : null),
            }}
          >
            {children}
          </Sheet.Scroller>
          {dockedFooter ? (
            <Box flexShrink={0} bg="bg" style={{ paddingBottom: scrollerPaddingBottom }}>
              {dockedFooter}
            </Box>
          ) : null}
        </Sheet.Content>

        {overlayFooter ? (
          <Box
            ref={measureOverlayFooter}
            position="absolute"
            insetX={0}
            bottom={0}
            zIndex={5}
            style={{ paddingBottom: scrollerPaddingBottom }}
          >
            <SheetFrost edge="bottom" elevated={elevated} />
            {overlayFooter}
          </Box>
        ) : null}

        {floatingAction ? (
          <Box
            position="absolute"
            bottom="calc(1.5rem + env(safe-area-inset-bottom, 0px))"
            zIndex={5}
            pointerEvents="none"
            {...(floatingActionPlacement === 'center'
              ? {
                  left: 'calc(1rem + env(safe-area-inset-left, 0px))',
                  right: 'calc(1rem + env(safe-area-inset-right, 0px))',
                  display: 'flex',
                  justifyContent: 'center',
                }
              : {
                  right: 'calc(1rem + env(safe-area-inset-right, 0px))',
                })}
          >
            <Box pointerEvents="auto">{floatingAction}</Box>
          </Box>
        ) : null}
      </Sheet.Container>

      {backdropChildren}
    </Sheet>
    </SheetHeaderSlotContext.Provider>
  );
}
